import React, { FC } from 'react';
import { Box, Button, Avatar, Typography, IconButton, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Training } from '../../../models/training.models';
import { UserAvatar } from '../../user-avatar/user-avatar.component';
import { palette } from '../../../theme';

export interface TrainingModalHeaderProps {
  training: Training;
  trainingType: 'athlete' | 'coach' | 'guardian';
  isLoading?: boolean;
  onClose?: () => void;
  menuButton?: React.ReactNode;
  markTrainingComplete?: () => void;
  wardName?: string;
}

const iconButtonHoverSx = {
  '&:hover': {
    backgroundColor: palette.action.hover,
  },
};

const markCompleteHoverSx = {
  textTransform: 'none' as const,
  borderRadius: '20px',
  fontWeight: 500,
  '&:hover': {
    borderColor: palette.primary.dark,
    backgroundColor: palette.primary.light,
  },
};

const TrainingModalHeader: FC<TrainingModalHeaderProps> = ({
  training,
  trainingType,
  isLoading,
  onClose,
  menuButton,
  markTrainingComplete,
  wardName,
}) => {
  const isComplete = training?.workoutData?.complete ?? false;
  const isPersonal = !training.team;
  const assigneeName = training.assignee?.name;

  return (
    <Box
      sx={{
        backgroundColor: palette.background.paper,
        flexShrink: 0,
        px: 2.5,
        pt: 2,
      }}
    >
      {/* Guardian banner */}
      {trainingType === 'guardian' && (training.ward?.name || wardName) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mx: -2.5,
            mt: -2,
            mb: 1.5,
            px: 2.5,
            py: 0.75,
            backgroundColor: palette.warning.light,
            borderBottom: `1px solid ${palette.warning.main}`,
          }}
        >
          <PersonOutlineIcon sx={{ fontSize: 16, color: palette.warning.dark }} />
          <Typography variant="caption" sx={{ color: palette.warning.dark, fontWeight: 600 }}>
            Viewing as guardian for{' '}
            <Typography component="span" variant="caption" fontWeight={800} sx={{ color: palette.warning.dark }}>
              {training.ward?.name || wardName}
            </Typography>
          </Typography>
        </Box>
      )}

      {/* Club / personal identity row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          {isPersonal ? (
            <UserAvatar src={training.assignee?.profileImage} name={assigneeName} size={40} fontSize="0.875rem" />
          ) : (
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: palette.primary.main,
                borderRadius: '10px',
              }}
            >
              <DirectionsRunIcon fontSize="small" />
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            {isPersonal ? (
              <>
                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
                  {assigneeName || 'Personal Workout'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Personal Workout
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                  {training.team?.name}
                </Typography>
                {training.group?.name && (
                  <Typography variant="caption" color="text.secondary">
                    {training.group.name}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Right: Complete status / Mark Complete + menu + Close */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {isComplete ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckIcon fontSize="small" />}
              disabled
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                fontWeight: 500,
                color: `${palette.success.main} !important`,
                borderColor: `${palette.success.main} !important`,
              }}
            >
              Training Complete
            </Button>
          ) : (
            training.workoutData &&
            !isComplete && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CheckIcon fontSize="small" />}
                onClick={markTrainingComplete}
                disabled={isLoading}
                sx={markCompleteHoverSx}
              >
                Mark Training Complete
              </Button>
            )
          )}
          {menuButton &&
            (React.isValidElement<{ sx?: object }>(menuButton)
              ? React.cloneElement(menuButton, {
                  sx: { ...iconButtonHoverSx, ...(menuButton.props.sx ?? {}) },
                })
              : menuButton)}
          {onClose && (
            <IconButton size="small" onClick={onClose} aria-label="Close" sx={iconButtonHoverSx}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider />
    </Box>
  );
};

export default TrainingModalHeader;
